/**
 * LCFPISecurityModals.js
 * Session security: UserMenuDropdown, LockScreen, useInactivityTimer.
 * Part of the LCFPI pre-deployment stabilization sprint.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  User, Settings, Lock, LogOut, Eye, EyeOff, Loader, Shield,
  AlertTriangle,
} from "lucide-react";
import client from "@/api/client";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";

// ── Inactivity timeouts (ms) by role ────────────────────────────────────────
const TIMEOUTS = {
  admin: 10 * 60_000,
  lcfpi_gov: 15 * 60_000, government: 15 * 60_000,
  lcfpi_pm: 15 * 60_000, programme_manager: 15 * 60_000, lcfpi: 15 * 60_000,
  lcfpi_investor: 20 * 60_000, investor: 20 * 60_000,
};
const DEFAULT_TIMEOUT  = 15 * 60_000;
const WARNING_BEFORE   = 2  * 60_000;

function getTimeoutMs(role) {
  const r = (role || "").toLowerCase();
  for (const [k, v] of Object.entries(TIMEOUTS)) {
    if (r.includes(k)) return v;
  }
  return DEFAULT_TIMEOUT;
}

// ── Inactivity timer hook ────────────────────────────────────────────────────
export function useInactivityTimer({ role, onLock, onWarn, onClearWarn, active = true }) {
  const lockTimer = useRef(null);
  const warnTimer = useRef(null);
  const didWarn   = useRef(false);

  const reset = useCallback(() => {
    clearTimeout(lockTimer.current);
    clearTimeout(warnTimer.current);
    if (didWarn.current) { onClearWarn?.(); didWarn.current = false; }
    if (!active) return;

    const total  = getTimeoutMs(role);
    const warnAt = total - WARNING_BEFORE;

    if (warnAt > 0) {
      warnTimer.current = setTimeout(() => {
        didWarn.current = true;
        onWarn?.();
      }, warnAt);
    }
    lockTimer.current = setTimeout(() => {
      onLock?.();
    }, total);
  }, [role, active, onLock, onWarn, onClearWarn]);

  useEffect(() => {
    if (!active) return;
    const events = ["mousemove", "keydown", "scroll", "click", "touchstart"];
    events.forEach(e => document.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      events.forEach(e => document.removeEventListener(e, reset));
      clearTimeout(lockTimer.current);
      clearTimeout(warnTimer.current);
    };
  }, [reset, active]);

  // Expose reset so API calls can extend the timer
  return { resetTimer: reset };
}

// ── User avatar (initials) ───────────────────────────────────────────────────
function UserAvatar({ user, size = 26 }) {
  const initials = [user?.first_name?.[0], user?.last_name?.[0]]
    .filter(Boolean).join("").toUpperCase() ||
    user?.email?.[0]?.toUpperCase() || "U";
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: "#1d4ed8",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.38, fontWeight: 700, color: "#fff",
      border: "1.5px solid #2563eb", flexShrink: 0, cursor: "pointer",
      userSelect: "none",
    }}>
      {initials}
    </div>
  );
}

function getRoleLabel(user) {
  const map = {
    admin: "Administrator", lcfpi_gov: "Government Official",
    lcfpi_pm: "Programme Manager", lcfpi_investor: "Investor / DFI",
    lcfpi: "LCFPI Operator",
  };
  const r = user?.active_role || user?.role || "";
  return map[r] || r.replace(/_/g, " ");
}

function HoverMenuItem({ icon: Icon, label, testId, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button onClick={onClick} data-testid={testId}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 10,
        padding: "8px 16px", background: hover ? "#1e293b" : "transparent",
        border: "none", color: "#94a3b8", fontSize: 12,
        cursor: "pointer", textAlign: "left", transition: "background .1s",
      }}>
      <Icon size={13} />{label}
    </button>
  );
}

function HoverMenuSignOut({ onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button onClick={onClick} data-testid="user-menu-signout"
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 10,
        padding: "8px 16px", background: hover ? "#1e293b" : "transparent",
        border: "none", color: hover ? "#f87171" : "#64748b", fontSize: 12,
        cursor: "pointer", textAlign: "left", transition: "background .1s, color .1s",
      }}>
      <LogOut size={13} />Sign Out
    </button>
  );
}

// ── User Menu Dropdown ───────────────────────────────────────────────────────
export function UserMenuDropdown({ onLock }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const logEvent = (action) =>
    client.post("/lcfpi/session-events", { action, metadata: {} }).catch(() => {});

  const handleSignOut = () => {
    logEvent("logout");
    setOpen(false);
    logout();
  };

  const handleLock = () => {
    logEvent("manual_lock");
    setOpen(false);
    onLock?.();
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="user-menu-trigger"
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "3px 8px 3px 3px", borderRadius: 20,
          background: open ? "#1e293b" : "transparent",
          border: `1px solid ${open ? "#334155" : "transparent"}`,
          cursor: "pointer", transition: "all .15s",
        }}
      >
        <UserAvatar user={user} size={26} />
        <span style={{
          fontSize: 10, color: "#94a3b8", maxWidth: 72,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {user?.first_name || user?.email?.split("@")[0] || "User"}
        </span>
      </button>

      {open && (
        <div
          data-testid="user-menu-dropdown"
          style={{
            position: "absolute", top: "calc(100% + 6px)", right: 0, width: 224,
            background: "#0d1726", border: "1px solid #1e293b", borderRadius: 14,
            boxShadow: "0 20px 40px rgba(0,0,0,.7)", zIndex: 10000, overflow: "hidden",
          }}
        >
          {/* User info */}
          <div style={{ padding: "14px 16px 12px", borderBottom: "1px solid #1e293b" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <UserAvatar user={user} size={32} />
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: "#f1f5f9", lineHeight: 1.3 }}>
                  {user?.first_name && user?.last_name
                    ? `${user.first_name} ${user.last_name}` : user?.email}
                </p>
                <p style={{ fontSize: 10, color: "#64748b", marginTop: 1 }}>
                  {getRoleLabel(user)}
                </p>
              </div>
            </div>
            {(user?.organization || user?.org_name) && (
              <p style={{
                fontSize: 10, color: "#475569", background: "#1e293b",
                padding: "3px 8px", borderRadius: 6, wordBreak: "break-word",
              }}>
                {user.organization || user.org_name}
              </p>
            )}
          </div>

          {/* Menu items */}
          <div style={{ padding: "6px 0" }}>
            <HoverMenuItem icon={User} label="My Profile" testId="user-menu-profile"
              onClick={() => { setOpen(false); toast.info("Profile settings — coming soon"); }} />
            <HoverMenuItem icon={Settings} label="Account Settings" testId="user-menu-settings"
              onClick={() => { setOpen(false); toast.info("Account settings — coming soon"); }} />
          </div>

          <div style={{ borderTop: "1px solid #1e293b", padding: "6px 0" }}>
            <HoverMenuItem icon={Lock} label="Lock Session" testId="user-menu-lock" onClick={handleLock} />
          </div>

          <div style={{ borderTop: "1px solid #1e293b", padding: "6px 0 8px" }}>
            <HoverMenuSignOut onClick={handleSignOut} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Lock Screen ──────────────────────────────────────────────────────────────
export function LockScreen({ onUnlocked }) {
  const { user, login, logout } = useAuth();
  const [password, setPassword]   = useState("");
  const [showPw,   setShowPw]     = useState(false);
  const [loading,  setLoading]    = useState(false);
  const [error,    setError]      = useState("");

  const unlock = async (e) => {
    e.preventDefault();
    if (!password.trim()) { setError("Password required"); return; }
    setLoading(true);
    setError("");
    try {
      await login(user?.email, password);
      client.post("/lcfpi/session-events", { action: "unlock", metadata: {} }).catch(() => {});
      onUnlocked?.();
    } catch {
      setError("Incorrect password — try again");
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = () => {
    client.post("/lcfpi/session-events", { action: "logout", metadata: {} }).catch(() => {});
    logout();
  };

  return (
    <div
      data-testid="lock-screen"
      style={{
        position: "fixed", inset: 0, zIndex: 99999, background: "#040b16",
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Subtle grid background */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: "radial-gradient(circle at 1px 1px, #1e293b 1px, transparent 0)",
        backgroundSize: "32px 32px", opacity: 0.35,
      }} />

      <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 380, padding: "0 24px" }}>
        <img src={LOGO_URL} alt="DeCarbonIQ"
          style={{ height: 34, margin: "0 auto 28px", display: "block", filter: "brightness(0) invert(1)" }} />

        <div style={{
          background: "#0d1726", border: "1px solid #1e293b",
          borderRadius: 20, padding: "32px 28px", marginBottom: 16,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14, background: "#1e293b",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 20px",
          }}>
            <Lock size={22} color="#60a5fa" />
          </div>

          <p style={{ fontSize: 16, fontWeight: 800, color: "#f1f5f9", textAlign: "center", marginBottom: 4 }}>
            Session Locked
          </p>
          <p style={{ fontSize: 12, color: "#64748b", textAlign: "center", marginBottom: 2 }}>
            {user?.first_name && user?.last_name
              ? `${user.first_name} ${user.last_name}` : user?.email}
          </p>
          <p style={{ fontSize: 11, color: "#475569", textAlign: "center", marginBottom: 24 }}>
            {user?.email}
          </p>

          <form onSubmit={unlock}>
            <div style={{ position: "relative", marginBottom: error ? 8 : 16 }}>
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={e => { setPassword(e.target.value); setError(""); }}
                placeholder="Enter your password to unlock"
                data-testid="lock-screen-password"
                autoFocus
                style={{
                  width: "100%", background: "#0d1f3a",
                  border: `1px solid ${error ? "#ef4444" : "#1e293b"}`,
                  borderRadius: 12, padding: "11px 40px 11px 14px",
                  fontSize: 13, color: "#f1f5f9", outline: "none",
                  boxSizing: "border-box",
                }}
              />
              <button type="button" onClick={() => setShowPw(s => !s)}
                style={{
                  position: "absolute", right: 12, top: "50%",
                  transform: "translateY(-50%)", background: "none",
                  border: "none", color: "#475569", cursor: "pointer", padding: 0,
                }}>
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>

            {error && (
              <p style={{ fontSize: 11, color: "#ef4444", marginBottom: 12, textAlign: "left" }}>
                {error}
              </p>
            )}

            <button
              type="submit" disabled={loading}
              data-testid="lock-screen-unlock-btn"
              style={{
                width: "100%", padding: 12, borderRadius: 12, border: "none",
                background: "#1d4ed8", color: "#fff", fontSize: 13, fontWeight: 700,
                cursor: "pointer", opacity: loading ? 0.6 : 1,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              {loading ? <Loader size={13} style={{ animation: "spin 1s linear infinite" }} /> : <Shield size={13} />}
              {loading ? "Verifying…" : "Unlock Session"}
            </button>
          </form>
        </div>

        <button onClick={handleSignOut} data-testid="lock-screen-signout"
          style={{
            display: "block", margin: "0 auto", fontSize: 11, color: "#475569",
            background: "none", border: "none", cursor: "pointer",
          }}
          onMouseEnter={e => e.currentTarget.style.color = "#94a3b8"}
          onMouseLeave={e => e.currentTarget.style.color = "#475569"}>
          Sign out instead
        </button>
      </div>
    </div>
  );
}

// ── Inactivity Warning Banner ────────────────────────────────────────────────
export function InactivityWarning({ onContinue, onLock }) {
  return (
    <div
      data-testid="inactivity-warning"
      style={{
        position: "fixed", bottom: 24, right: 24, zIndex: 9998,
        background: "#0d1726", border: "1px solid #92400e",
        borderRadius: 14, padding: "14px 18px", maxWidth: 320,
        boxShadow: "0 8px 24px rgba(0,0,0,.6)",
        display: "flex", flexDirection: "column", gap: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <AlertTriangle size={14} color="#fbbf24" />
        <p style={{ fontSize: 12, fontWeight: 700, color: "#fbbf24" }}>
          Session locking in 2 minutes
        </p>
      </div>
      <p style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.5 }}>
        Your session will lock due to inactivity. Move the mouse or press any key to continue.
      </p>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onContinue} data-testid="inactivity-continue-btn"
          style={{
            flex: 1, padding: "7px 0", borderRadius: 8, border: "1px solid #1e40af",
            background: "#1d4ed820", color: "#93c5fd", fontSize: 11,
            fontWeight: 600, cursor: "pointer",
          }}>
          Continue Session
        </button>
        <button onClick={onLock} data-testid="inactivity-lock-now-btn"
          style={{
            padding: "7px 14px", borderRadius: 8, border: "1px solid #334155",
            background: "transparent", color: "#64748b", fontSize: 11, cursor: "pointer",
          }}>
          Lock Now
        </button>
      </div>
    </div>
  );
}
